/**
 * NotificationBell — notification bell button with unread count badge.
 *
 * Phase 9 / Plan 09-04 — renders in AppShell's headerRight slot.
 * Shows a Bell icon with an unread count badge.
 * Clicking opens a shadcn Sheet (right-side drawer).
 *
 * Two sources, one badge (Phase 09 / 22, OPEN-085 follow-up):
 *   - "For your role" — server notifications from GET /v1/notifications: new operator haul
 *     requests, a driver over the per-shift reject limit, auto-escalated InReview tickets.
 *     Clicking one marks it read and, for a job, opens that job's detail modal. Read state
 *     is shared by the whole role, and the drawer says so.
 *   - "Rejections seen this session" — the browser-local notices useRejectionPoller
 *     records (DISPATCH-05). They are not stored anywhere and clear on reload. A
 *     driver over the reject limit shows in both sections, but as two different
 *     events (one rejection vs. the shift count), each with its own label.
 * "Mark all read" clears both.
 *
 * The bell itself and its read logic live in @lodex-pro/web-shared/notifications, shared with
 * web-billing. This file supplies what is dispatch-only: the local rejections section, the
 * job link, and the dispatcher's own timezone (DEC-004 / DEC-066).
 *
 * Spec: UI-SPEC §7 (notification bell); Copywriting; DISPATCH-05; T-09-04-06.
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  NOTIFICATION_SECTION_HEADING_STYLE,
  NOTIFICATION_TIME_STYLE,
  NotificationBell as SharedNotificationBell,
  useMarkNotificationRead,
  useServerNotifications,
  type InAppNotification,
  type NotificationLink,
} from '@lodex-pro/web-shared/notifications';
import { useNotificationStore, type RejectionNotice } from './notification-store';
import { JobDetailModal } from '../jobs/JobDetailModal';
import { formatTime, formatTimestamp } from '../../lib/timestamp';
import { useUserTimezone } from '../../lib/use-user-timezone';

function RejectionsSection({
  rejections,
  tz,
}: {
  rejections: readonly RejectionNotice[];
  tz: string;
}): React.JSX.Element {
  return (
    <section aria-label="Rejections seen this session">
      <h3 style={NOTIFICATION_SECTION_HEADING_STYLE}>Rejections seen this session</h3>
      {/* Show newest first */}
      {[...rejections].reverse().map((notice) => (
        <div
          key={`${notice.jobId}-${notice.at}`}
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--color-border-primary)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '8px',
            }}
          >
            <span
              style={{
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--color-danger)',
              }}
            >
              Job Rejected
            </span>
            <span style={NOTIFICATION_TIME_STYLE}>{formatTime(notice.at, tz)}</span>
          </div>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: '14px',
              color: 'var(--color-text-primary)',
              lineHeight: 1.5,
            }}
          >
            {notice.driverName} rejected{' '}
            <span
              style={{
                fontFamily: 'var(--font-mono, monospace)',
                color: 'var(--color-accent-blue)',
                fontSize: '12px',
              }}
            >
              {notice.haulId}
            </span>
          </p>
          {notice.reason.length > 0 && (
            <p
              style={{
                margin: '4px 0 0',
                fontSize: '12px',
                color: 'var(--color-text-tertiary)',
                fontStyle: 'italic',
              }}
            >
              {notice.reason}
            </p>
          )}
        </div>
      ))}
    </section>
  );
}

export function NotificationBell(): React.JSX.Element {
  const { rejections, unreadCount: localUnread, markAllRead } = useNotificationStore();
  const { data: serverData } = useServerNotifications();
  const markRead = useMarkNotificationRead();
  const tz = useUserTimezone();
  const [openJobId, setOpenJobId] = React.useState<string | null>(null);
  const navigate = useNavigate();

  // A job row opens the same detail modal the Job Board uses. A Paelode catalog notice (OPEN-146)
  // opens Sites & Operators, where the hauler's wells and customers are. Anything else only
  // marks read.
  const linkFor = (n: InAppNotification): NotificationLink | null => {
    if (
      n.relatedEntityType?.startsWith('CATALOG_') === true &&
      n.relatedEntityType !== 'CATALOG_CHANGE'
    ) {
      return {
        label: 'Open Sites & Operators',
        open: (): void => {
          void navigate('/sites-operators');
        },
      };
    }
    const jobId = n.relatedEntityId;
    if (n.relatedEntityType !== 'job' || jobId === null) return null;
    return {
      label: 'Open job',
      open: (): void => {
        setOpenJobId(jobId);
      },
    };
  };

  return (
    <>
      <SharedNotificationBell
        rows={serverData?.notifications}
        onMarkRead={(id): void => {
          markRead.mutate(id);
        }}
        formatWhen={(iso): string => formatTimestamp(iso, tz)}
        linkFor={linkFor}
        local={{
          unread: localUnread,
          isEmpty: rejections.length === 0,
          markAllRead,
          section: <RejectionsSection rejections={rejections} tz={tz} />,
        }}
      />

      {/* A job notification opens the same detail modal the Job Board uses. */}
      {openJobId !== null && (
        <JobDetailModal
          jobId={openJobId}
          onClose={(): void => {
            setOpenJobId(null);
          }}
        />
      )}
    </>
  );
}
